import {
    faLock,
    faArrowsUpDown,
    faChevronDown,
    faChevronUp,
    faXmark,
    faPlus,
    faCheck
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { FC, useEffect, useState, SyntheticEvent } from "react";
import {
    Button,
    Card,
    Col,
    Collapse,
    FormControl,
    InputGroup,
    Row
} from "react-bootstrap";
import {
    AnalyticsEventDataSchema,
    AnalyticsEventSchema
} from "../../../models/types";
import { AnalyticsEventDataItem } from "./AnalyticsEventDataItem";
import cx from "classnames";

interface Props {
    index: number;
    hasDuplicatesEvents: boolean;
    isEqualToLib: boolean;
    defaultValue: AnalyticsEventSchema;
    updateAnalyticsSchema: (
        index: number,
        event?: AnalyticsEventSchema
    ) => void;
    saveToLibrary: (eventData: AnalyticsEventSchema) => void;
    dragStart: () => void;
    dragEnter: () => void;
    dragEnd: () => void;
    isDragged: boolean;
}

export const AnalyticsEventItemImplementation: FC<Props> = ({
    index,
    hasDuplicatesEvents,
    isEqualToLib,
    defaultValue,
    saveToLibrary,
    updateAnalyticsSchema,
    dragStart,
    dragEnter,
    dragEnd,
    isDragged
}) => {
    const [event, updateEvent] = useState(defaultValue);
    const [onEditing, updateOnEditing] = useState(false);
    const [draggable, setDraggable] = useState(false);
    const [showEventData, updateShowEventData] = useState(false);
    const updateSchema = () => {
        if (JSON.stringify(event) !== JSON.stringify(defaultValue)) {
            updateAnalyticsSchema(index, event);
        }
    };
    const remove = () => {
        updateAnalyticsSchema(index, undefined);
    };
    const [isDragging, setIsDragging] = useState(false);

    useEffect(() => {
        if (JSON.stringify(event) !== JSON.stringify(defaultValue)) {
            updateEvent(defaultValue);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultValue]);

    const updateAnalyticsDataSchema = (
        dataIndex: number,
        eventData?: AnalyticsEventDataSchema | undefined
    ) => {
        if (eventData) {
            const newEvents = [...event.eventData];
            newEvents[dataIndex] = eventData;
            updateAnalyticsSchema(index, { ...event, eventData: newEvents });
        } else {
            const newEvents = [
                ...event.eventData.slice(0, dataIndex),
                ...event.eventData.slice(dataIndex + 1)
            ];
            updateAnalyticsSchema(index, { ...event, eventData: newEvents });
        }
    };

    const addNewEventData = () => {
        const newEvents = [
            ...event.eventData,
            {
                key: "new key...",
                values: [1, 2]
            }
        ];
        updateAnalyticsSchema(index, { ...event, eventData: newEvents });
    };

    const findDuplicatesEventData = (eventData: AnalyticsEventDataSchema) => {
        if (!event.eventData) return false;
        const duplicates = event.eventData.filter(element => {
            return element.key === eventData.key;
        });
        return duplicates.length > 1;
    };

    const eventData = event.eventData?.map(
        (eventData: AnalyticsEventDataSchema, index: number) => (
            <AnalyticsEventDataItem
                frozen={event.frozen}
                index={index}
                hasDuplicatesKey={findDuplicatesEventData(eventData)}
                key={index}
                defaultValue={eventData}
                updateAnalyticsDataSchema={updateAnalyticsDataSchema}
            />
        )
    );

    useEffect(() => {
        const handleMouseUp = () => {
            if (isDragging) {
                setIsDragging(false);
                setDraggable(false);
                dragEnd();
            }
        };
        window.addEventListener("mouseup", handleMouseUp);
        return () => {
            window.removeEventListener("mouseup", handleMouseUp);
        };
    }, [isDragging, dragEnd]);

    const getSaveButtonTitle = () => {
        if (hasDuplicatesEvents) {
            return "Cannot save: Duplicate events";
        } else if (onEditing) {
            return "Cannot save: Currently editing";
        } else if (isEqualToLib) {
            return "Cannot save: Event already exists in the library";
        }
        return "";
    };

    return (
        <Card
            className={cx(
                "m-2 inner-card",
                { "ms-3 dragging-border": !event.frozen && isDragged },
                { "border-danger": hasDuplicatesEvents }
            )}
            draggable={!event.frozen && draggable}
            onDragStart={(event: any) => {
                const ghostElement = document.createElement("div");
                event.dataTransfer.setDragImage(ghostElement, 0, 0);
                updateShowEventData(false);
                dragStart();
                setIsDragging(true);
            }}
            onDragEnter={() => {
                updateShowEventData(false);
                dragEnter();
            }}
            onDrop={() => {
                setIsDragging(false);
                dragEnd();
                const ghostElement = document.querySelector(".ghostElement");
                if (ghostElement) {
                    ghostElement.remove();
                }
            }}
            onDragOver={(e: SyntheticEvent) => e.preventDefault()}
        >
            <Card.Header
                className="no-border"
                onMouseDown={(event: SyntheticEvent) => {
                    setDraggable(true);
                }}
                onMouseUp={(event: SyntheticEvent) => setDraggable(false)}
            >
                <Row className="d-flex align-items-center g-2">
                    <Col className="d-flex align-items-center col-7 col-md-6">
                        {!event.frozen ? (
                            <span className="d-none d-md-block">
                                <FontAwesomeIcon
                                    title="Drag and drop to re-order"
                                    icon={faArrowsUpDown}
                                    size="lg"
                                    className={
                                        isDragged
                                            ? "text-primary me-3"
                                            : "text-subtle me-3  mouse-grab"
                                    }
                                />
                            </span>
                        ) : (
                            <span className="d-none d-md-block">
                                <FontAwesomeIcon
                                    title="Position locked"
                                    icon={faLock}
                                    className="text-muted me-3 cursor-pointer"
                                />
                            </span>
                        )}
                        <InputGroup>
                            <InputGroup.Text
                                className={
                                    hasDuplicatesEvents ? "text-danger" : ""
                                }
                                title={
                                    hasDuplicatesEvents
                                        ? "Duplicate Event Name!"
                                        : ""
                                }
                            >
                                <span className="d-none d-md-block">Event</span>
                                <span>&nbsp;Name</span>
                            </InputGroup.Text>
                            <FormControl
                                title={
                                    hasDuplicatesEvents
                                        ? "Duplicate Event Name!"
                                        : ""
                                }
                                className={
                                    hasDuplicatesEvents ? "text-danger" : ""
                                }
                                value={event.eventName}
                                disabled={event.frozen}
                                onClick={(e: React.MouseEvent<HTMLElement>) => {
                                    e.stopPropagation();
                                }}
                                onMouseDown={(
                                    e: React.MouseEvent<HTMLElement>
                                ) => e.stopPropagation()}
                                onChange={(changeEvent: any) => {
                                    const newName = changeEvent.target.value;
                                    updateOnEditing(true);
                                    updateEvent({
                                        ...event,
                                        eventName: newName
                                    });
                                }}
                                onBlur={() => {
                                    updateSchema();
                                }}
                                placeholder="Enter new event name"
                            />
                        </InputGroup>
                    </Col>
                    <Col
                        className="col-1 ps-2 ps-lg-3"
                        onClick={() => updateShowEventData(!showEventData)}
                    >
                        <FontAwesomeIcon
                            icon={showEventData ? faChevronUp : faChevronDown}
                            title={
                                showEventData
                                    ? "Hide Event data"
                                    : "Show Event data"
                            }
                            className="text-muted mouseHover"
                        />
                    </Col>
                    <Col className="col-3 col-md-4 d-flex justify-content-end align-items-center">
                        {!event.frozen ? (
                            <>
                                <Button
                                    onClick={() => {
                                        if (!showEventData) {
                                            updateShowEventData(true);
                                        }
                                        addNewEventData();
                                    }}
                                    className="btn-sm"
                                >
                                    <FontAwesomeIcon icon={faPlus} size="lg" />
                                </Button>
                                <span className="ps-2 d-none d-md-block bread-text text-subtle">
                                    Event Data
                                </span>
                                <Button
                                    onClick={() => {
                                        saveToLibrary(event);
                                    }}
                                    className="btn-sm btn-analytics ms-2 ms-sm-4"
                                    disabled={
                                        hasDuplicatesEvents ||
                                        onEditing ||
                                        isEqualToLib
                                    }
                                    title={getSaveButtonTitle()}
                                >
                                    <FontAwesomeIcon
                                        icon={isEqualToLib ? faCheck : faPlus}
                                        size="lg"
                                    />
                                </Button>
                                <span className="ps-2 d-none d-md-block bread-text text-subtle">
                                    {isEqualToLib
                                        ? "Saved to Library"
                                        : "Save to Library"}
                                </span>
                            </>
                        ) : null}
                    </Col>
                    <Col className="col-1 d-flex align-items-center justify-content-end pe-1">
                        {event.frozen ? (
                            <FontAwesomeIcon
                                icon={faLock}
                                className="text-muted"
                                title="Event frozen by Admin"
                            />
                        ) : (
                            <FontAwesomeIcon
                                icon={faXmark}
                                title="Remove Event"
                                size="lg"
                                className="text-subtle mouseHover"
                                onClick={(event: SyntheticEvent) => {
                                    event.stopPropagation();
                                    remove();
                                }}
                            />
                        )}
                    </Col>
                </Row>
            </Card.Header>
            <Collapse in={showEventData}>
                <div>
                    <Card.Body className="p-2">
                        <Row className="d-flex justify-content-start ms-3 g-0">
                            {eventData}
                        </Row>
                        <div className="px-4 py-2 mx-3 mt-2">
                            <InputGroup>
                                <InputGroup.Text>Comment</InputGroup.Text>
                                <FormControl
                                    as="textarea"
                                    rows={2}
                                    value={event.comment ? event.comment : ""}
                                    placeholder="Description of the event and/or event data"
                                    disabled={event.frozen}
                                    onChange={(changeEvent: any) => {
                                        const newComment =
                                            changeEvent.target.value;
                                        updateEvent({
                                            ...event,
                                            comment: newComment
                                        });
                                    }}
                                    onBlur={() => {
                                        updateSchema();
                                    }}
                                />
                            </InputGroup>
                        </div>
                    </Card.Body>
                </div>
            </Collapse>
        </Card>
    );
};

export const AnalyticsEventItem = React.memo(AnalyticsEventItemImplementation);
